import { getApiKeyOverview, createApiKey, reportPublicApiUsage } from 'src/App/Dashboards/Api/api';

// Types
export interface ApiKeyData {
  key_id: string;
  key_hash: string;
  key_name: string;
  user_id: string;
  status: string;
  created_at: number;
  credits_remaining: number;
  tier: string;
}

export interface UsageData {
  grouped_usage_data: {
    [date: string]: {
      [endpoint: string]: number;
    };
  };
  total_usage_count: number;
  total_cost: number;
}

// Action Types
export const API_ACTIONS = {
  INITIALIZE_DATA_START: 'API_INITIALIZE_DATA_START',
  INITIALIZE_DATA_SUCCESS: 'API_INITIALIZE_DATA_SUCCESS',
  INITIALIZE_DATA_ERROR: 'API_INITIALIZE_DATA_ERROR',
  REFRESH_DATA_START: 'API_REFRESH_DATA_START',
  REFRESH_DATA_SUCCESS: 'API_REFRESH_DATA_SUCCESS',
  REFRESH_DATA_ERROR: 'API_REFRESH_DATA_ERROR',
  UPDATE_USAGE_START: 'API_UPDATE_USAGE_START',
  UPDATE_USAGE_SUCCESS: 'API_UPDATE_USAGE_SUCCESS',
  UPDATE_USAGE_ERROR: 'API_UPDATE_USAGE_ERROR',
  CLEAR_ERROR: 'API_CLEAR_ERROR',
};

// Action Creators
export const initializeApiDataStart = () => ({
  type: API_ACTIONS.INITIALIZE_DATA_START,
});

export const initializeApiDataSuccess = (apiKeyData: ApiKeyData, usageData: UsageData) => ({
  type: API_ACTIONS.INITIALIZE_DATA_SUCCESS,
  payload: { apiKeyData, usageData },
});

export const initializeApiDataError = (error: string) => ({
  type: API_ACTIONS.INITIALIZE_DATA_ERROR,
  payload: error,
});

export const refreshApiDataStart = () => ({
  type: API_ACTIONS.REFRESH_DATA_START,
});

export const refreshApiDataSuccess = (apiKeyData: ApiKeyData, usageData: UsageData) => ({
  type: API_ACTIONS.REFRESH_DATA_SUCCESS,
  payload: { apiKeyData, usageData },
});

export const refreshApiDataError = (error: string) => ({
  type: API_ACTIONS.REFRESH_DATA_ERROR,
  payload: error,
});

export const updateUsageDataStart = () => ({
  type: API_ACTIONS.UPDATE_USAGE_START,
});

export const updateUsageDataSuccess = (usageData: UsageData) => ({
  type: API_ACTIONS.UPDATE_USAGE_SUCCESS,
  payload: usageData,
});

export const updateUsageDataError = (error: string) => ({
  type: API_ACTIONS.UPDATE_USAGE_ERROR,
  payload: error,
});

export const clearApiError = () => ({
  type: API_ACTIONS.CLEAR_ERROR,
});

// Async Action Creators
export const initializeApiData = () => {
  return async (dispatch: any) => {
    dispatch(initializeApiDataStart());
    try {
      // First, check if user has an existing API key
      let apiKeyInfo = await getApiKeyOverview();

      if (!apiKeyInfo) {
        // If no API key exists, create a new one
        console.log('No existing API key found, creating new one...');
        apiKeyInfo = await createApiKey('Default API Key');

        if (!apiKeyInfo) {
          throw new Error('Failed to create API key');
        }
      }

      // Get usage data for the last 30 days
      const usage = await reportPublicApiUsage(30);

      dispatch(initializeApiDataSuccess(apiKeyInfo, usage));
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to initialize API data';
      dispatch(initializeApiDataError(errorMessage));
    }
  };
};

export const refreshApiData = () => {
  return async (dispatch: any) => {
    dispatch(refreshApiDataStart());
    try {
      // Refresh both API key data and usage data
      const apiKeyInfo = await getApiKeyOverview();
      const usage = await reportPublicApiUsage(30);

      if (!apiKeyInfo) {
        throw new Error('No API key found');
      }

      dispatch(refreshApiDataSuccess(apiKeyInfo, usage));
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to refresh API data';
      dispatch(refreshApiDataError(errorMessage));
    }
  };
};

export const updateUsageData = (days: number) => {
  return async (dispatch: any) => {
    dispatch(updateUsageDataStart());
    try {
      const usage = await reportPublicApiUsage(days);
      dispatch(updateUsageDataSuccess(usage));
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to update usage data';
      dispatch(updateUsageDataError(errorMessage));
    }
  };
};
